import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { API } from "../../lib/api";
import { normalizeImageUrl } from "../../lib/imageUrl";
import { CheckCircle, WarningCircle, PaperPlaneTilt, UploadSimple } from "@phosphor-icons/react";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [reminderLog, setReminderLog] = useState([]);
  const [qbSending, setQbSending] = useState(false);
  const loc = useLocation();
  const scrolledHashRef = useRef(null);

  useEffect(() => { api.get("/admin/settings").then(r => { setS(r.data); setTestTo(r.data.admin_notify_email || ""); }); api.get("/admin/reminders/log").then(r => setReminderLog(r.data)).catch(() => {}); }, []);

  // Smooth-scroll to deep-linked section ONLY on hash change (don't re-scroll on every save).
  useEffect(() => {
    if (!s || !loc.hash) return;
    if (scrolledHashRef.current === loc.hash) return;
    const id = loc.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      scrolledHashRef.current = loc.hash;
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [loc.hash, s]);

  if (!s) return <div className="p-12 text-ms-stone">Loading…</div>;

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", patch);
      setS(prev => ({ ...prev, ...r.data }));
    } finally { setSaving(false); }
  };

  // Bind a settings key to an input/textarea — auto-save on blur, spellcheck off (so "St. Petersburg" stops getting flagged)
  const bind = (key) => ({
    value: s[key] || "",
    onChange: (e) => setS(prev => ({ ...prev, [key]: e.target.value })),
    onBlur: () => save({ [key]: s[key] }),
    spellCheck: false,
    "data-testid": `settings-${key.replace(/_/g, "-")}`,
  });

  // Generic image upload — saves a RELATIVE URL (e.g. "/api/files/...") so the image
  // works on any domain (preview, mysteryserved.com, future custom domains).
  const uploadImageFor = (key) => async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url; // relative path like "/api/files/..."
      setS(prev => ({ ...prev, [key]: url }));
      await save({ [key]: url });
    } finally { setUploading(prev => ({ ...prev, [key]: false })); }
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/admin/test-email", { to: testTo });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ sent: false, error: e.response?.data?.detail || "Failed" });
    } finally { setTesting(false); }
  };

  return (
    <div className="p-5 md:p-12 max-w-4xl" data-testid="admin-settings">
      <p className="label-eyebrow text-ms-stone">Admin</p>
      <h1 className="serif text-4xl text-ms-earth mt-2">Settings</h1>

      {/* Homepage hero — fully editable */}
      <section id="homepage" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 md:mt-8 scroll-mt-24" data-testid="settings-homepage">
        <p className="label-eyebrow text-ms-brass">Homepage · Hero</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">Hero copy & image</h2>
        <p className="text-xs text-ms-stone mt-1.5">The big section at the top of the homepage. Saves automatically when you click out of a field.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow (small label above headline)"><input {...bind("home_eyebrow")} className="field-input" /></Field>
          <Field label="CTA primary button label"><input {...bind("home_cta_primary_label")} className="field-input" /></Field>
          <Field label="Headline — line 1 (dark)"><input {...bind("home_headline_1")} className="field-input" /></Field>
          <Field label="Headline — line 2 (italic brass accent)"><input {...bind("home_headline_2")} className="field-input" /></Field>
          <Field label="CTA secondary button label"><input {...bind("home_cta_secondary_label")} className="field-input" /></Field>
        </div>

        <div className="mt-4">
          <Field label="Subhead (paragraph under the headline)"><textarea rows={3} {...bind("home_subhead")} className="field-input" /></Field>
        </div>

        <div className="mt-5">
          <ImageField label="Hero image" url={s.home_hero_image} onUpload={uploadImageFor("home_hero_image")} onUrlChange={bind("home_hero_image")} uploading={uploading.home_hero_image} hint="Recommended: warm, sunlit, human-led photo (people exploring, maps, hands-on detail). Min 1600px wide." testIdPrefix="home-hero" />
        </div>
      </section>

      {/* Intro section */}
      <section id="intro" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-intro">
        <p className="label-eyebrow text-ms-brass">Homepage · Intro</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">"An invitation" section</h2>
        <p className="text-xs text-ms-stone mt-1.5">Two-column section with text on the left and three photos on the right.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input {...bind("intro_eyebrow")} className="field-input" /></Field>
          <Field label="Inline link label"><input {...bind("intro_cta_label")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Headline (large serif)"><textarea rows={2} {...bind("intro_headline")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Body paragraph"><textarea rows={5} {...bind("intro_body")} className="field-input" /></Field>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <ImageField label="Main image (large left)" url={s.intro_image_main} onUpload={uploadImageFor("intro_image_main")} onUrlChange={bind("intro_image_main")} uploading={uploading.intro_image_main} testIdPrefix="intro-main" compact />
          <ImageField label="Top right (small)" url={s.intro_image_top} onUpload={uploadImageFor("intro_image_top")} onUrlChange={bind("intro_image_top")} uploading={uploading.intro_image_top} testIdPrefix="intro-top" compact />
          <ImageField label="Bottom right (small)" url={s.intro_image_bottom} onUpload={uploadImageFor("intro_image_bottom")} onUrlChange={bind("intro_image_bottom")} uploading={uploading.intro_image_bottom} testIdPrefix="intro-bottom" compact />
        </div>
      </section>

      {/* How it works (3 steps) */}
      <section id="steps" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-steps">
        <p className="label-eyebrow text-ms-brass">Homepage · How it works</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">Three numbered steps</h2>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input {...bind("steps_eyebrow")} className="field-input" /></Field>
          <Field label="Section headline"><input {...bind("steps_headline")} className="field-input" /></Field>
        </div>

        {[1,2,3].map(n => (
          <div key={n} className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 pt-5 border-t border-ms-edge">
            <Field label={`Step ${n} — title`}><input {...bind(`step${n}_title`)} className="field-input" /></Field>
            <div className="md:col-span-2">
              <Field label={`Step ${n} — body`}><textarea rows={3} {...bind(`step${n}_body`)} className="field-input" /></Field>
            </div>
          </div>
        ))}
      </section>

      {/* Featured tours section header */}
      <section id="featured" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-featured">
        <p className="label-eyebrow text-ms-brass">Homepage · Featured tours</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">"St. Pete Adventures" section header</h2>
        <p className="text-xs text-ms-stone mt-1.5">The eyebrow + headline that sit above the tour cards on the homepage.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input {...bind("featured_eyebrow")} className="field-input" /></Field>
          <Field label="Headline"><input {...bind("featured_headline")} className="field-input" /></Field>
        </div>
      </section>

      {/* /tours page header */}
      <section id="tours-index" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-tours-index">
        <p className="label-eyebrow text-ms-brass">Public site · Tours index (/tours)</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">Tours page hero copy</h2>
        <p className="text-xs text-ms-stone mt-1.5">The header on mysteryserved.com/tours. Edit any of these without touching code.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input {...bind("tours_index_eyebrow")} className="field-input" data-testid="settings-tours-eyebrow" /></Field>
          <Field label="Headline"><input {...bind("tours_index_headline")} className="field-input" data-testid="settings-tours-headline" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Subtitle / intro paragraph">
            <textarea rows={3} {...bind("tours_index_subtitle")} className="field-input" data-testid="settings-tours-subtitle" />
          </Field>
        </div>
      </section>

      {/* Bottom CTA band */}
      <section id="cta" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-cta">
        <p className="label-eyebrow text-ms-brass">Homepage · Closing call-to-action</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">Bottom "Ready to book" band</h2>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input {...bind("cta_eyebrow")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Headline (large)"><textarea rows={2} {...bind("cta_headline")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Body paragraph"><textarea rows={3} {...bind("cta_body")} className="field-input" /></Field>
        </div>
        <div className="mt-5">
          <ImageField label="Side image" url={s.cta_image} onUpload={uploadImageFor("cta_image")} onUrlChange={bind("cta_image")} uploading={uploading.cta_image} testIdPrefix="cta-image" />
        </div>
      </section>

      {/* Marketing toggle (testimonials etc.) */}
      <section id="marketing" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-marketing">
        <p className="label-eyebrow text-ms-brass">Homepage · Marketing sections</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">Show / hide sections</h2>
        <label className="mt-5 flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={!!s.show_testimonials} onChange={e=>{ const v = e.target.checked; setS(prev => ({...prev, show_testimonials:v})); save({show_testimonials:v}); }} className="mt-1 accent-ms-brass" data-testid="settings-show-testimonials" />
          <div>
            <p className="text-ms-ink text-sm font-medium">Show testimonials section</p>
            <p className="text-ms-stone text-xs mt-1 leading-[1.6]">When off, the "From the case files" testimonials block is hidden on the public homepage. Turn this back on once you have real reviews.</p>
          </div>
        </label>
      </section>

      {/* ABOUT page editor */}
      <section id="about" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-about">
        <p className="label-eyebrow text-ms-brass">Site · About page</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">About page content</h2>
        <p className="text-xs text-ms-stone mt-1.5">Lives at <a href="/about" target="_blank" rel="noreferrer" className="underline hover:text-ms-brass">/about</a>. Saves on click-out.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input {...bind("about_eyebrow")} className="field-input" /></Field>
          <Field label="CTA button label"><input {...bind("about_cta_label")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Hero headline (large white serif)"><textarea rows={2} {...bind("about_hero_headline")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <ImageField label="Hero image (defaults to your homepage hero if blank)" url={s.about_hero_image} onUpload={uploadImageFor("about_hero_image")} onUrlChange={bind("about_hero_image")} uploading={uploading.about_hero_image} testIdPrefix="about-hero" />
        </div>

        <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4 pt-5 border-t border-ms-edge">
          <Field label="Section eyebrow"><input {...bind("about_section_eyebrow")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Section headline"><textarea rows={2} {...bind("about_section_headline")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Body (separate paragraphs with a blank line)"><textarea rows={8} {...bind("about_body")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <ImageField label="Side image (large vertical photo on the left)" url={s.about_side_image} onUpload={uploadImageFor("about_side_image")} onUrlChange={bind("about_side_image")} uploading={uploading.about_side_image} testIdPrefix="about-side" />
        </div>
      </section>

      {/* FOOTER — logo, contact, socials, quote, copyright */}
      <section id="footer" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-footer">
        <p className="label-eyebrow text-ms-brass">Site · Footer</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">Footer content</h2>
        <p className="text-xs text-ms-stone mt-1.5">Logo, tagline, quote, contact info, social links, and copyright. Saves on click-out. Empty fields stay hidden on the live site.</p>

        <div className="mt-6">
          <ImageField label="Footer logo (transparent PNG works best, ~256px tall)" url={s.footer_logo} onUpload={uploadImageFor("footer_logo")} onUrlChange={bind("footer_logo")} uploading={uploading.footer_logo} testIdPrefix="footer-logo" />
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Tagline (italic line under logo)"><input {...bind("footer_tagline")} className="field-input" /></Field>
          <Field label="Quote attribution (e.g. Frank Zappa)"><input {...bind("footer_quote_attribution")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Quote (no surrounding quotes — they'll be added)"><textarea rows={2} {...bind("footer_quote")} className="field-input" /></Field>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Contact email"><input type="email" {...bind("footer_email")} className="field-input" placeholder="info@mysteryserved.com" /></Field>
          <Field label="Phone (optional)"><input {...bind("footer_phone")} className="field-input" placeholder="+1 (727) 555-0100" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Address (optional, supports multiple lines)"><textarea rows={2} {...bind("footer_address")} className="field-input" /></Field>
        </div>

        <p className="label-eyebrow text-ms-stone text-[10px] mt-7">Social links (leave blank to hide)</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Facebook URL"><input {...bind("footer_facebook_url")} className="field-input" placeholder="https://facebook.com/mysteryserved" /></Field>
          <Field label="Instagram URL"><input {...bind("footer_instagram_url")} className="field-input" placeholder="https://instagram.com/mysteryserved" /></Field>
          <Field label="TikTok URL"><input {...bind("footer_tiktok_url")} className="field-input" placeholder="https://tiktok.com/@mysteryserved" /></Field>
          <Field label="YouTube URL"><input {...bind("footer_youtube_url")} className="field-input" placeholder="https://youtube.com/@mysteryserved" /></Field>
          <Field label="X (Twitter) URL"><input {...bind("footer_x_url")} className="field-input" placeholder="https://x.com/mysteryserved" /></Field>
        </div>

        <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Copyright line (year is added automatically)"><input {...bind("footer_copyright")} className="field-input" /></Field>
          <Field label="Terms link label"><input {...bind("footer_terms_label")} className="field-input" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Terms link URL (e.g. /about or full URL)"><input {...bind("footer_terms_url")} className="field-input" /></Field>
        </div>
      </section>

      {/* SEO — page title & meta description */}
      <section id="seo" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24" data-testid="settings-seo">
        <p className="label-eyebrow text-ms-brass">Search engine</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">SEO</h2>
        <p className="text-xs text-ms-stone mt-1.5">What shows in the browser tab and on Google search results. Saves on click-out.</p>

        <div className="mt-5 space-y-4">
          <Field label="Page title (browser tab + Google headline)">
            <input value={s.seo_title || ""} onChange={e=>setS({...s, seo_title:e.target.value})} onBlur={()=>save({seo_title:s.seo_title})} className="field-input" data-testid="settings-seo-title" />
            <p className="text-[11px] text-ms-stone mt-1.5">{(s.seo_title || "").length} characters · keep under 60 for full display in search results.</p>
          </Field>
          <Field label="Meta description (Google preview snippet)">
            <textarea rows={3} value={s.seo_description || ""} onChange={e=>setS({...s, seo_description:e.target.value})} onBlur={()=>save({seo_description:s.seo_description})} className="field-input" data-testid="settings-seo-description" />
            <p className="text-[11px] text-ms-stone mt-1.5">{(s.seo_description || "").length} characters · sweet spot is 140–160.</p>
          </Field>
          <Field label="Custom head code (verification tags, analytics scripts)">
            <textarea rows={4} value={s.custom_head_code || ""} onChange={e=>setS({...s, custom_head_code:e.target.value})} onBlur={()=>save({custom_head_code:s.custom_head_code})} className="field-input font-mono text-xs" placeholder={'<meta name="google-site-verification" content="..." />'} data-testid="settings-custom-head-code" />
            <p className="text-[11px] text-ms-stone mt-1.5">Pasted here, this code is added to the &lt;head&gt; of every public page — e.g. Google Search Console verification meta tags or analytics snippets. Saves on click-out.</p>
          </Field>
        </div>
      </section>

      {/* Stripe section */}
      <section id="payments" className="bg-white border border-ms-edge rounded-2xl p-7 mt-8 scroll-mt-24">
        <p className="label-eyebrow text-ms-brass">Payments — Stripe</p>
        <h2 className="serif text-2xl text-ms-earth mt-2">Stripe mode</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ModeCard
            active={!s.live_mode}
            title="Test mode"
            desc="Use the Stripe test key. Cards are simulated. Use this during development & testing."
            badge={s.stripe_test_configured ? "configured" : "missing"}
            ok={s.stripe_test_configured}
            onClick={() => save({ live_mode: false })}
            testId="stripe-mode-test"
          />
          <ModeCard
            active={!!s.live_mode}
            title="Live mode"
            desc="Real charges on real cards. Only enable after testing is complete."
            badge={s.stripe_live_configured ? "configured" : "key missing"}
            ok={s.stripe_live_configured}
            onClick={() => {
              if (!s.stripe_live_configured) {
                alert("STRIPE_LIVE_KEY is not set in /app/backend/.env. Add it and restart backend before enabling live mode.");
                return;
              }
              if (!window.confirm("Switch to LIVE mode? Real cards will be charged.")) return;
              save({ live_mode: true });
            }}
            testId="stripe-mode-live"
          />
        </div>
        <p className="mt-5 text-xs text-ms-stone leading-relaxed">
          To set keys: edit <code className="bg-ms-cream px-1.5 py-0.5 rounded text-ms-ink">/app/backend/.env</code> →
          set <code className="bg-ms-cream px-1.5 py-0.5 rounded text-ms-ink">STRIPE_TEST_KEY</code> and
          <code className="bg-ms-cream px-1.5 py-0.5 rounded text-ms-ink ml-1">STRIPE_LIVE_KEY</code>, then restart backend.
        </p>
      </section>

      {/* Email section */}
      <section id="email" className="bg-white border border-ms-edge rounded-2xl p-5 md:p-7 mt-6 scroll-mt-24">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="label-eyebrow text-ms-brass">Transactional email</p>
            <h2 className="serif text-2xl text-ms-earth mt-2">From address</h2>
          </div>
          <Status ok={s.email_transport === "gmail" || s.email_transport === "resend"} okLabel={s.email_transport === "gmail" ? "Sending via Gmail (Google Workspace)" : "Sending via Resend"} badLabel="No email transport — mock mode" />
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Sender email">
            <input value={s.sender_email || ""} onChange={e=>setS({...s, sender_email:e.target.value})} onBlur={()=>save({sender_email:s.sender_email})} className="field-input" data-testid="settings-sender-email" />
          </Field>
          <Field label="Sender name">
            <input value={s.sender_name || ""} onChange={e=>setS({...s, sender_name:e.target.value})} onBlur={()=>save({sender_name:s.sender_name})} className="field-input" data-testid="settings-sender-name" />
          </Field>
          <Field label="Admin notification email">
            <input value={s.admin_notify_email || ""} onChange={e=>setS({...s, admin_notify_email:e.target.value})} onBlur={()=>save({admin_notify_email:s.admin_notify_email})} className="field-input" data-testid="settings-admin-email" />
          </Field>
          <Field label="Admin notification phone (SMS, needs Twilio)">
            <input value={s.admin_notify_phone || ""} onChange={e=>setS({...s, admin_notify_phone:e.target.value})} onBlur={()=>save({admin_notify_phone:s.admin_notify_phone})} placeholder="(727) 555-0000" className="field-input" data-testid="settings-admin-phone" />
          </Field>
        </div>

        {/* Brand logo for all transactional emails */}
        <div className="mt-5">
          <Field label="Email brand logo (URL — leave empty to hide)">
            <input
              value={s.email_logo_url || ""}
              onChange={e=>setS({...s, email_logo_url:e.target.value})}
              onBlur={()=>save({email_logo_url:s.email_logo_url})}
              placeholder="https://mysteryserved.com/logo.png"
              className="field-input"
              data-testid="settings-email-logo-url"
            />
            <p className="text-ms-stone text-[11px] mt-1.5 leading-[1.5]">
              Shown at the top of every email Mystery Served sends. Use a publicly accessible PNG/JPG —
              it will be sized to max 200×80 px. To swap for bachelorette parties (or any sub-brand)
              later, just paste a different URL here and save.
            </p>
            {s.email_logo_url && (
              <div className="mt-3 inline-block bg-ms-cream border border-ms-edge rounded-md p-4">
                <p className="label-eyebrow text-ms-stone text-[10px] mb-2">Preview</p>
                <img src={s.email_logo_url} alt="Logo preview" style={{maxWidth: 200, maxHeight: 80}} data-testid="settings-email-logo-preview" />
              </div>
            )}
          </Field>
        </div>

        <div className="mt-7 pt-6 border-t border-ms-edge">
          <p className="label-eyebrow text-ms-stone text-xs">Send test email</p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <input value={testTo} onChange={e=>setTestTo(e.target.value)} placeholder="recipient@example.com" className="field-input flex-1 min-w-[260px]" data-testid="test-email-to" />
            <button onClick={sendTest} disabled={testing || !testTo} className="btn-primary disabled:opacity-50" data-testid="test-email-send">
              <PaperPlaneTilt size={14} /> {testing ? "Sending…" : "Send test"}
            </button>
          </div>
          {testResult && (
            <div className={`mt-4 p-4 rounded-md text-sm ${testResult.sent ? "bg-ms-success/10 text-ms-earth" : (testResult.mocked ? "bg-ms-warn/10 text-ms-earth" : "bg-ms-danger/10 text-ms-danger")}`} data-testid="test-email-result">
              {testResult.sent && <>✓ Sent successfully to {testResult.to}. (Resend ID: {testResult.id})</>}
              {testResult.mocked && <>⚠ {testResult.message}. Add RESEND_API_KEY to backend .env and restart.</>}
              {!testResult.sent && !testResult.mocked && <>✗ {testResult.error || "Send failed."}</>}
            </div>
          )}
        </div>

        <div className="mt-7 pt-6 border-t border-ms-edge">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="label-eyebrow text-ms-brass text-xs">Automated balance reminders</p>
              <p className="text-xs text-ms-stone mt-1.5">Unpaid co-guests get an automatic nudge by email — and by SMS once Twilio is connected — until they pay. The job runs hourly in the background.</p>
            </div>
            <Status ok={s.sms_configured} okLabel="Twilio SMS connected" badLabel="Twilio keys missing — email only" />
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Auto reminders">
              <button
                onClick={() => { const v = !s.auto_reminders_enabled; setS({...s, auto_reminders_enabled: v}); save({auto_reminders_enabled: v}); }}
                className={`w-full py-2.5 rounded-md text-sm border transition-all ${s.auto_reminders_enabled ? "bg-ms-ink text-ms-cream border-ms-ink" : "bg-white text-ms-stone border-ms-edge hover:bg-ms-cream"}`}
                data-testid="settings-auto-reminders-toggle"
              >
                {s.auto_reminders_enabled ? "On — reminding automatically" : "Off"}
              </button>
            </Field>
            <Field label="Remind every (days)">
              <input type="number" min={1} max={30} value={s.reminder_interval_days ?? 3}
                onChange={e=>setS({...s, reminder_interval_days: parseInt(e.target.value || "3", 10)})}
                onBlur={()=>save({reminder_interval_days: s.reminder_interval_days})}
                className="field-input" data-testid="settings-reminder-interval" />
            </Field>
            <Field label="Public site URL (used in reminder links)">
              <input value={s.public_site_url || ""} onChange={e=>setS({...s, public_site_url:e.target.value})} onBlur={()=>save({public_site_url:s.public_site_url})} className="field-input" data-testid="settings-public-site-url" />
            </Field>
            <Field label="Game link email — hours before start">
              <input type="number" min={1} max={48} value={s.game_link_lead_hours ?? 3}
                onChange={e=>setS({...s, game_link_lead_hours: parseInt(e.target.value || "3", 10)})}
                onBlur={()=>save({game_link_lead_hours: s.game_link_lead_hours})}
                className="field-input" data-testid="settings-game-link-hours" />
            </Field>
          </div>

          {/* Post-tour review capture */}
          <div className="mt-8 pt-6 border-t border-ms-edge" data-testid="settings-review-section">
            <p className="label-eyebrow text-ms-brass text-xs">Post-tour Google reviews</p>
            <p className="text-sm text-ms-stone mt-1.5 leading-relaxed max-w-2xl">
              Automatically email the lead booker 24 hours after each tour ends, asking for a Google review. Per-tour opt-out lives on each tour's edit page (useful for private events).
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Auto-send review request">
                <button
                  type="button"
                  onClick={() => { const v = !s.review_capture_enabled; setS({...s, review_capture_enabled: v}); save({review_capture_enabled: v}); }}
                  className={`w-full py-2.5 rounded-md text-sm border transition-all ${s.review_capture_enabled ? "bg-ms-ink text-ms-cream border-ms-ink" : "bg-white text-ms-stone border-ms-edge hover:bg-ms-cream"}`}
                  data-testid="settings-review-toggle"
                >
                  {s.review_capture_enabled ? "On — asking after every tour" : "Off"}
                </button>
              </Field>
              <Field label="Delay (hours after tour ends)">
                <input
                  type="number" min={1} max={168}
                  value={s.review_capture_delay_hours ?? 24}
                  onChange={e => setS({...s, review_capture_delay_hours: parseInt(e.target.value || "24", 10)})}
                  onBlur={() => save({ review_capture_delay_hours: s.review_capture_delay_hours })}
                  className="field-input"
                  data-testid="settings-review-delay"
                />
              </Field>
            </div>
            <Field label="Google review URL (paste from Google Business → Ask for reviews)">
              <input
                value={s.google_review_url || ""}
                onChange={e => setS({...s, google_review_url: e.target.value})}
                onBlur={() => save({ google_review_url: s.google_review_url })}
                placeholder="https://g.page/r/..."
                className="field-input"
                data-testid="settings-review-url"
              />
            </Field>
          </div>

          {/* QuickBooks bookkeeping export — moved to /admin/bookkeeping */}
          <div className="mt-8 pt-6 border-t border-ms-edge bg-ms-cream/30 rounded-md p-5" data-testid="settings-qb-moved-notice">
            <p className="label-eyebrow text-ms-brass text-xs">QuickBooks bookkeeping export</p>
            <p className="text-sm text-ms-stone mt-1.5 leading-relaxed">
              This now lives in its own section. → <a href="/admin/bookkeeping" className="text-ms-brass underline">Open Bookkeeping</a>
            </p>
          </div>

          <div className="mt-6">
            <p className="label-eyebrow text-ms-stone text-xs mb-2">Reminder activity</p>
            {reminderLog.length === 0 ? (
              <p className="text-xs text-ms-stone" data-testid="reminder-log-empty">No reminders sent yet. Activity will appear here once a co-guest is nudged.</p>
            ) : (
              <div className="border border-ms-edge rounded-lg overflow-hidden" data-testid="reminder-log">
                <table className="w-full text-xs">
                  <thead className="bg-ms-cream border-b border-ms-edge">
                    <tr>
                      <th className="text-left p-3 label-eyebrow text-[10px]">Guest</th>
                      <th className="text-left p-3 label-eyebrow text-[10px]">Tour</th>
                      <th className="text-left p-3 label-eyebrow text-[10px]">Channels</th>
                      <th className="text-left p-3 label-eyebrow text-[10px]">Trigger</th>
                      <th className="text-left p-3 label-eyebrow text-[10px]">When</th>
                      <th className="text-left p-3 label-eyebrow text-[10px]">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reminderLog.slice(0, 25).map(r => (
                      <tr key={r.id} className="border-b border-ms-edge last:border-0" data-testid={`reminder-log-row-${r.id}`}>
                        <td className="p-3 text-ms-ink">{r.guest_name}<span className="text-ms-stone"> · {r.guest_email}</span></td>
                        <td className="p-3 text-ms-stone">{r.tour_title}</td>
                        <td className="p-3 text-ms-stone uppercase">{(r.channels || []).join(" + ")}</td>
                        <td className="p-3">{r.trigger === "auto" ? <span className="text-ms-brass">Auto</span> : <span className="text-ms-stone">Manual</span>}</td>
                        <td className="p-3 text-ms-stone">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                        <td className="p-3">{r.success ? <span className="text-ms-success">Sent</span> : <span className="text-ms-danger">Failed</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="mt-7 pt-6 border-t border-ms-edge">
          <p className="label-eyebrow text-ms-brass text-xs">How sending works</p>
          <ol className="mt-3 list-decimal list-inside text-sm text-ms-stone space-y-2 leading-relaxed">
            <li><strong className="text-ms-ink">Gmail SMTP (active when configured)</strong> — emails send through your Google Workspace account <code className="bg-ms-cream px-1.5 py-0.5 rounded text-ms-ink">info@mysteryserved.com</code> using an app password. No DNS records needed. Limit ~2,000 emails/day.</li>
            <li>If the Gmail app password is ever revoked, the system automatically falls back to Resend (requires DNS-verified domain), then to safe mock mode.</li>
            <li>To rotate the app password: myaccount.google.com → Security → App passwords → create new → update <code className="bg-ms-cream px-1.5 py-0.5 rounded text-ms-ink">GMAIL_SMTP_APP_PASSWORD</code> in the backend environment.</li>
            <li>Click "Send test" above any time to confirm the pipeline.</li>
          </ol>
        </div>
      </section>

      {saving && <p className="mt-4 text-xs text-ms-stone">Saving…</p>}
    </div>
  );
}

function ModeCard({ active, title, desc, badge, ok, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-left p-5 rounded-md border transition-all ${active ? "border-ms-brass bg-ms-cream" : "border-ms-edge bg-white hover:border-ms-stone"}`}>
      <div className="flex items-center justify-between">
        <p className="text-ms-earth text-sm font-medium">{title}</p>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${ok ? "bg-ms-success/15 text-ms-success" : "bg-ms-danger/15 text-ms-danger"}`}>{badge}</span>
      </div>
      <p className="text-ms-stone text-xs mt-2 leading-[1.55]">{desc}</p>
      {active && <p className="mt-3 text-[10px] label-eyebrow text-ms-brass">Active</p>}
    </button>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="label-eyebrow text-ms-stone text-[10px]">{label}</span><div className="mt-2">{children}</div></label>;
}
function ImageField({ label, url, onUpload, onUrlChange, uploading, hint, testIdPrefix, compact }) {
  const ref = useRef(null);
  const displayUrl = normalizeImageUrl(url);
  return (
    <div>
      <p className="label-eyebrow text-ms-stone text-[10px]">{label}</p>
      <div className={`mt-3 ${compact ? "" : "flex items-start gap-4 flex-wrap"}`}>
        {displayUrl && (
          <img src={displayUrl} alt="" className={compact ? "w-full aspect-[4/3] object-cover rounded-md border border-ms-edge mb-2" : "w-40 h-32 object-cover rounded-md border border-ms-edge"} />
        )}
        <div className={compact ? "space-y-2" : "flex-1 min-w-[260px] space-y-2"}>
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="btn-secondary w-full justify-center" data-testid={`settings-${testIdPrefix}-upload`}>
            <UploadSimple size={14} /> {uploading ? "Uploading…" : "Upload image"}
          </button>
          <input ref={ref} type="file" accept="image/*" hidden onChange={onUpload} />
          <input {...onUrlChange} placeholder="…or paste a URL" className="field-input" />
          {hint && <p className="text-xs text-ms-stone leading-relaxed">{hint}</p>}
        </div>
      </div>
    </div>
  );
}
function Status({ ok, okLabel, badLabel }) {
  return ok
    ? <span className="inline-flex items-center gap-1.5 text-xs text-ms-success"><CheckCircle size={14} weight="duotone" /> {okLabel}</span>
    : <span className="inline-flex items-center gap-1.5 text-xs text-ms-warn"><WarningCircle size={14} weight="duotone" /> {badLabel}</span>;
}
